import type { ReactNode } from "react"

import { useTranslation } from "react-i18next"

import type { PlanSectionView } from "./plan-utils"
import type { FieldCitation, PlanSource } from "../../types"

import { cn } from "@/lib/utils"

import { DataGapsCallout, FreshDraftBanner } from "./plan-callouts"
import { SourcesList } from "./plan-field"
import { PlanTile } from "./plan-tile"
import { citationAnchorId, resolveCitationIndex } from "./plan-utils"

/**
 * Document view: sticky section nav on the left, stacked section cards on the
 * right, then the Sources list. Citation chips scroll to and flash their source.
 */
export function PlanDocumentView({
  planKind,
  sections,
  renderBody,
  sources,
  dataGaps,
  freshDraft,
  canEdit,
  onUpdate,
  updateTestId,
}: {
  planKind: string
  sections: PlanSectionView[]
  renderBody: (fieldKey: string, onCitationClick?: (citation: FieldCitation) => void) => ReactNode
  sources: PlanSource[]
  dataGaps: string[]
  freshDraft: boolean
  canEdit: boolean
  onUpdate: (fieldKey: string, title: string) => void
  updateTestId: (fieldKey: string) => string
}) {
  const { t } = useTranslation()

  function handleCitationClick(citation: FieldCitation) {
    // Resolve by identity, not by the citation's own index: LLM citations carry
    // a sourceId but no index, so defaulting to 1 sent every chip to the first
    // source in a globally deduplicated list.
    const index = resolveCitationIndex(citation, sources)
    if (index === null) return
    const el = document.getElementById(citationAnchorId(planKind, "sources", index))
    if (!el) return
    el.scrollIntoView({ block: "center", behavior: "smooth" })
    el.classList.add("ring-2", "ring-primary")
    window.setTimeout(() => el.classList.remove("ring-2", "ring-primary"), 1200)
  }

  function scrollToSection(no: string) {
    document
      .getElementById(`sa-section-${no}`)
      ?.scrollIntoView({ block: "start", behavior: "smooth" })
  }

  return (
    <div className="grid gap-6 lg:grid-cols-[220px_1fr]">
      <nav
        aria-label="Section navigation"
        className="top-20 hidden self-start lg:sticky lg:block"
        data-testid="sa-plan-section-nav"
      >
        <ul className="grid gap-0.5">
          {sections.map((section) => (
            <li key={section.no}>
              <button
                type="button"
                className={cn(
                  "text-muted-foreground hover:bg-accent hover:text-foreground w-full rounded px-2 py-1.5 text-left text-sm transition"
                )}
                onClick={() => scrollToSection(section.no)}
              >
                <span className="text-muted-foreground/60 mr-1.5">{section.no}</span>
                {section.title}
              </button>
            </li>
          ))}
        </ul>
      </nav>
      <div className="min-w-0 space-y-4">
        {freshDraft ? <FreshDraftBanner /> : null}
        <DataGapsCallout labels={dataGaps} />
        {sections.map((section) => (
          <PlanTile
            key={section.no}
            canEdit={canEdit}
            colSpan={section.colSpan}
            mode="document"
            no={section.no}
            rowSpan={section.rowSpan}
            title={section.title}
            updatable={section.updatable}
            updateTestId={updateTestId(section.fieldKey)}
            onUpdate={
              section.updatable ? () => onUpdate(section.fieldKey, section.title) : undefined
            }
          >
            {renderBody(section.fieldKey, handleCitationClick)}
          </PlanTile>
        ))}
        <SourcesList planKind={planKind} sources={sources} />
        {sources.length === 0 ? (
          <p className="text-muted-foreground text-xs">{t("segmentAnalysis.plan.noSourcesYet")}</p>
        ) : null}
      </div>
    </div>
  )
}
